import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  adminGetPromoStats,
  adminListPromosLite,
} from "../../../Admin/adminApi";
import "./Promozioni.css";
import "./AdminPromozioniStats.css";
import useCityAutocomplete from "../../../hooks/useCityAutocomplete";
import Profilo from "../../Profilo";

function eurFromCents(cents) {
  const n = Number(cents || 0) / 100;
  return n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

export default function AdminPromozioniSegmentiStats() {
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    days: 90,
    supplierId: "",
    promoId: "",
    city: "",
    province: "",
    q: "",
    page: 1,
    pageSize: 200,
  });

  const [overview, setOverview] = useState(null);
  const [promos, setPromos] = useState([]);
  const [serverFilters, setServerFilters] = useState(null);

  const [promoOptions, setPromoOptions] = useState([]);
  const [promoOptionsLoading, setPromoOptionsLoading] = useState(false);

  const [segments, setSegments] = useState({
    last_used: [],
    last_expired: [],
    last_active_unused: [],
  });
  const [segTab, setSegTab] = useState("last_active_unused");

  const [openClientId, setOpenClientId] = useState(null);

  function openClientDetail(id) {
    if (!id) return;
    setOpenClientId(id);
  }

  function closeClientDetail() {
    setOpenClientId(null);
  }

  const cityAuto = useCityAutocomplete({
    citta: filters.city,
    provincia: filters.province,
    cap: "",
    enabled: true,
  });

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setPromoOptionsLoading(true);
        const res = await adminListPromosLite();
        if (!alive) return;
        setPromoOptions(Array.isArray(res?.promos) ? res.promos : []);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setPromoOptions([]);
      } finally {
        if (alive) setPromoOptionsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const loadStats = async () => {
    const supplierIdNum =
      String(filters.supplierId || "").trim() === ""
        ? null
        : Number(filters.supplierId);

    const noPromo = filters.promoId === "__none__";
    const promoIdNum =
      !noPromo && String(filters.promoId || "").trim() !== ""
        ? Number(filters.promoId)
        : null;

    const res = await adminGetPromoStats({
      days: Number(filters.days) || 90,
      expiringDays: Number(filters.expiringDays) || 7,
      supplierId: Number.isFinite(supplierIdNum) ? supplierIdNum : null,
      q: filters.q || "",
      promoId: Number.isFinite(promoIdNum) ? promoIdNum : null,
      noPromo,
      city: (filters.city || "").trim() || null,
      province: (filters.province || "").trim() || null,
      page: Number(filters.page) || 1,
      pageSize: Number(filters.pageSize) || 50,
    });

    setServerFilters(res?.filters || null);
    setOverview(res?.overview || null);
    setPromos(Array.isArray(res?.promos) ? res.promos : []);

    setSegments({
      last_used: Array.isArray(res?.customers_lastpromo_segments?.last_used)
        ? res.customers_lastpromo_segments.last_used
        : [],
      last_expired: Array.isArray(
        res?.customers_lastpromo_segments?.last_expired,
      )
        ? res.customers_lastpromo_segments.last_expired
        : [],
      last_active_unused: Array.isArray(
        res?.customers_lastpromo_segments?.last_active_unused,
      )
        ? res.customers_lastpromo_segments.last_active_unused
        : [],
    });
  };

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        await loadStats();
      } catch (e) {
        console.error("[PromoSegmentiStats] load error:", e);
        toast.error(e?.message || "Errore caricamento segmenti promo");
        if (!alive) return;
        setOverview(null);
        setPromos([]);
        setServerFilters(null);
        setSegments({
          last_used: [],
          last_expired: [],
          last_active_unused: [],
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.promoId,
    filters.city,
    filters.province,
    filters.days,
    filters.expiringDays,
    filters.supplierId,
    filters.q,
    filters.page,
    filters.pageSize,
  ]);

  const viewSeg = useMemo(() => {
    const k = String(segTab || "last_active_unused");
    return Array.isArray(segments?.[k]) ? segments[k] : [];
  }, [segments, segTab]);

  return (
    <div className="promo-page aps-page">
      <div className="promo-shell">
        <div className="promo-shell-inner">
          <div className="promo-page-section">
            <div className="promo-toolbar promo-toolbar--page">
              <div className="promo-toolbar-left">
                <label className="promo-check">
                  <span>Promo</span>
                  <select
                    className="promo-create-select"
                    value={filters.promoId}
                    onChange={(e) =>
                      setFilters((p) => ({
                        ...p,
                        promoId: e.target.value,
                        page: 1,
                      }))
                    }
                    title="Filtra per promo"
                    disabled={promoOptionsLoading}
                  >
                    <option value="">Tutte</option>
                    <option value="__none__">Nessuna promo</option>
                    {promoOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        #{p.id} · {p.name || "—"} {p.code ? `· ${p.code}` : ""}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="promo-check">
                  <span>Finestra</span>
                  <select
                    className="promo-create-select"
                    value={filters.days}
                    onChange={(e) =>
                      setFilters((p) => ({
                        ...p,
                        days: Number(e.target.value),
                        page: 1,
                      }))
                    }
                  >
                    <option value={7}>7 giorni</option>
                    <option value={30}>30 giorni</option>
                    <option value={90}>90 giorni</option>
                    <option value={365}>365 giorni</option>
                  </select>
                </label>
              </div>

              <div className="aps-city-wrap">
                <label className="promo-check aps-city">
                  <span>Città</span>
                  <input
                    className="promo-search"
                    placeholder="Es. Milano"
                    value={filters.city}
                    onChange={(e) => {
                      const nextCity = e.target.value;
                      setFilters((p) => ({
                        ...p,
                        city: nextCity,
                        province: p.province ? "" : p.province,
                        page: 1,
                      }));
                      cityAuto.setSuggestions([]);
                    }}
                  />
                </label>

                {cityAuto.suggestions?.length > 0 &&
                  (filters.city || "").trim().length >= 2 && (
                    <div className="aps-city-dd">
                      {cityAuto.suggestions.slice(0, 8).map((s, idx) => {
                        const c =
                          s?.name || s?.citta || s?.city || s?.nome || "";
                        const pr =
                          s?.provincia || s?.province || s?.sigla || "";
                        const label = pr ? `${c} (${pr})` : c;

                        return (
                          <button
                            key={`${c}-${pr}-${idx}`}
                            type="button"
                            className="aps-city-dd-item"
                            onClick={() => {
                              setFilters((p) => ({
                                ...p,
                                city: c,
                                province: pr || p.province,
                                page: 1,
                              }));
                              cityAuto.setSuggestions([]);
                            }}
                            title={label}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )}
              </div>

              <label className="promo-check">
                <span>Provincia</span>
                <input
                  className="promo-search"
                  style={{ maxWidth: 110 }}
                  placeholder="Es. MI"
                  value={filters.province}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      province: e.target.value,
                      page: 1,
                    }))
                  }
                />
              </label>

              <div className="promo-toolbar-right">
                <span className="muted">
                  {serverFilters
                    ? `days=${serverFilters.days}`
                    : loading
                    ? "carico…"
                    : ""}
                </span>
              </div>
            </div>

            <div className="group-card" style={{ marginTop: 14 }}>
              <div className="group-head">
                <div className="gh-left plain">
                  <div className="gh-title">
                    Segmenti · ultima promo inviata
                  </div>
                  <div className="gh-count">{viewSeg.length}</div>
                </div>
              </div>

              <div className="group-body">
                <div className="promo-toolbar promo-toolbar--page aps-segbar">
                  <div className="promo-toolbar-left">
                    <button
                      type="button"
                      className={`promo-btn ${
                        segTab === "last_used"
                          ? "promo-btn--primary"
                          : "promo-btn--ghost"
                      }`}
                      onClick={() => setSegTab("last_used")}
                    >
                      Usata ({segments?.last_used?.length || 0})
                    </button>

                    <button
                      type="button"
                      className={`promo-btn ${
                        segTab === "last_expired"
                          ? "promo-btn--primary"
                          : "promo-btn--ghost"
                      }`}
                      onClick={() => setSegTab("last_expired")}
                    >
                      Scaduta ({segments?.last_expired?.length || 0})
                    </button>

                    <button
                      type="button"
                      className={`promo-btn ${
                        segTab === "last_active_unused"
                          ? "promo-btn--primary"
                          : "promo-btn--ghost"
                      }`}
                      onClick={() => setSegTab("last_active_unused")}
                    >
                      Attiva ({segments?.last_active_unused?.length || 0})
                    </button>
                  </div>

                  <div className="promo-toolbar-right">
                    <span className="muted">
                      Segmento: <b>{segTab}</b>
                    </span>
                  </div>
                </div>

                {!loading && viewSeg.length === 0 && (
                  <div className="promo-empty">
                    Nessun cliente in questo segmento.
                  </div>
                )}

                {!loading && viewSeg.length > 0 && (
                  <div className="table-wrap">
                    <div className="table table-stats-segments">
                      <div className="thead">
                        <div>Cliente</div>
                        <div className="ta-center">Ultima promo</div>
                        <div>Scadenza</div>
                        <div>Usata</div>
                        <div className="ta-center">Comprato dopo</div>
                        <div className="ta-right">Ordini dopo</div>
                      </div>

                      {viewSeg.map((r) => (
                        <div className="trow" key={r.user_id}>
                          <div className="cell-name">
                            <div className="name-wrap">
                              {r.user_id ? (
                                <button
                                  type="button"
                                  className="aps-link-btn"
                                  onClick={() => openClientDetail(r.user_id)}
                                  title={`Apri cliente #${r.user_id}`}
                                >
                                  <span className="name-text">
                                    {r.full_name || `Utente #${r.user_id}`}
                                  </span>
                                </button>
                              ) : (
                                <span className="name-text">
                                  {r.full_name || `Utente #${r.user_id}`}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="ta-center cell-promo">
                            <span
                              className="name-text"
                              title={r.promo_name || ""}
                            >
                              {r.promo_name || "—"}
                            </span>
                          </div>
                          <div className="muted">
                            {fmtDate(r.effective_until)}
                          </div>

                          <div className="muted">
                            {r.used_at ? fmtDate(r.used_at) : "—"}
                          </div>

                          <div className="ta-center">
                            <span
                              className={`p-badge ${
                                r.bought_after ? "on" : "off"
                              }`}
                            >
                              {r.bought_after ? "SI" : "NO"}
                            </span>
                          </div>

                          <div className="ta-right">
                            {Number(r.orders_after_count || 0)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* fine segmenti */}
          </div>
        </div>
      </div>
      {openClientId && (
        <div className="ac-overlay" role="dialog" aria-modal="true">
          <div className="ac-modal">
            <div className="ac-modal-h">
              <div className="ac-title">Cliente #{openClientId}</div>
              <button className="ac-close" onClick={closeClientDetail}>
                ✕
              </button>
            </div>

            <div className="ac-modal-body">
              <div className="ac-form">
                <div className="ac-profile-embed">
                  <Profilo embedded targetUserId={openClientId} />
                </div>
              </div>
            </div>

            <div className="ac-modal-f">
              <button className="ac-btn" onClick={closeClientDetail}>
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
